(function () {
  if (!window.HbElements) return;

  function detailProductId(detail) {
    return detail && detail.productId != null ? String(detail.productId) : '';
  }

  function formatMoneyAmount(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '0.00';
    return (value / 100).toFixed(2);
  }

  function defaultSelectedOptions() {
    return [{ name: 'Title', value: 'Default Title' }];
  }

  function namedSelectedOptions(root) {
    const options = [];
    if (!root) return options;
    root.querySelectorAll('input[data-option-position]:checked').forEach((input) => {
      options.push({
        name: input.dataset.optionName || '',
        value: input.dataset.optionValue || input.value || '',
      });
    });
    return options;
  }

  function matchingHost(selector, productId) {
    const hosts = Array.from(document.querySelectorAll(selector));
    if (!productId) return hosts[0] || null;
    return (
      hosts.find((el) => {
        const id = el.dataset.productId || '';
        return !id || id === String(productId);
      }) || null
    );
  }

  function matchingVariantPicker(productId) {
    return matchingHost('hb-variant-picker', productId);
  }

  function matchingSellingPlans(productId) {
    return matchingHost('hb-selling-plans', productId);
  }

  function dispatchProductSelect(element, { variant, selectedOptions, detail } = {}) {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent || !element) return;

    const options = selectedOptions && selectedOptions.length ? selectedOptions : defaultSelectedOptions();
    const deferred = ProductSelectEvent.createPromise();
    const payload = {
      product: {
        id: element.dataset.productId,
        title: element.dataset.productTitle,
        handle: element.dataset.productHandle,
      },
      selectedOptions: options,
      promise: deferred.promise,
    };
    if (detail) payload.detail = detail;

    element.dispatchEvent(new ProductSelectEvent(payload));

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: Boolean(variant.available),
          price: {
            amount: formatMoneyAmount(variant.price),
            currencyCode: element.dataset.currencyCode,
          },
          selectedOptions: options,
        },
      });
      return;
    }

    deferred.resolve({ variant: null });
  }

  function dispatchCartLinesAdd(element, lines) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent || !element || !lines || !lines.length) return null;

    const deferred = CartLinesUpdateEvent.createPromise();
    element.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise,
      })
    );
    return { deferred, CartLinesUpdateEvent };
  }

  function dispatchCartErrorEvent(element, message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent || !element) return;
    element.dispatchEvent(new CartErrorEvent({ error: message || '', code: code || 'INVALID' }));
  }

  function resolveCartLinesFromAjax(handle, maybeCart) {
    if (!handle) return Promise.resolve();

    const finish = (cart) => {
      if (!cart || !cart.currency) {
        handle.deferred.reject(new Error('Missing currency in cart response'));
        return;
      }
      handle.deferred.resolve({ cart: handle.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
    };

    if (maybeCart && maybeCart.currency) {
      finish(maybeCart);
      return Promise.resolve();
    }

    const pending =
      typeof CartItems !== 'undefined' && typeof CartItems.fetchCartData === 'function'
        ? CartItems.fetchCartData()
        : fetch(`${(window.routes && routes.cart_url) || '/cart'}.js`).then((response) => response.json());

    return Promise.resolve(pending)
      .then(finish)
      .catch((error) => handle.deferred.reject(error));
  }

  class HbProductForm extends HbElements.Component {
    onConnect() {
      this.productId = this.dataset.productId || '';
      this.form = this.querySelector('form[data-type="add-to-cart-form"]') || this.querySelector('form');
      this.variantInput = this.form ? this.form.querySelector('[name="id"]') : null;
      this.sellingPlanInput = this.form ? this.form.querySelector('input[name="selling_plan"]') : null;
      this.submitButton = this.form ? this.form.querySelector('[type="submit"][name="add"]') : null;
      this.submitLabel = this.submitButton ? this.submitButton.querySelector('span') : null;
      this.buyNowButton = this.querySelector('[data-buy-now]');
      this.defaultToSubscription = this.boolAttr('defaultToSubscription', false);
      this.defaultSellingPlanId = this.dataset.defaultSellingPlanId || '';
      this.currentSellingPlanId = this.sellingPlanInput ? this.sellingPlanInput.value : '';
      this.hasSellingPlansUi = Boolean(this.querySelector('hb-selling-plans'));

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onSellingPlanChange = this.onSellingPlanChange.bind(this);
      this.onBuyNowClick = this.onBuyNowClick.bind(this);
      this.onFormData = this.onFormData.bind(this);

      document.addEventListener('hb:variant-change', this.onVariantChange);
      document.addEventListener('hb:selling-plan-change', this.onSellingPlanChange);

      if (this.form) {
        this.form.addEventListener('formdata', this.onFormData);
      }

      if (this.buyNowButton) {
        this.buyNowButton.addEventListener('click', this.onBuyNowClick);
      }

      if (!this.hasSellingPlansUi) {
        this.applyFallbackSellingPlan();
      }
    }

    onDisconnect() {
      document.removeEventListener('hb:variant-change', this.onVariantChange);
      document.removeEventListener('hb:selling-plan-change', this.onSellingPlanChange);
      if (this.form) {
        this.form.removeEventListener('formdata', this.onFormData);
      }
      if (this.buyNowButton) {
        this.buyNowButton.removeEventListener('click', this.onBuyNowClick);
      }
    }

    matchesProduct(detail) {
      const id = detailProductId(detail);
      if (!this.productId || !id) return true;
      return id === String(this.productId);
    }

    onVariantChange(event) {
      const detail = event.detail || {};
      if (!this.matchesProduct(detail)) return;
      this.applyVariant(detail.variant, detail.variantId);
    }

    onSellingPlanChange(event) {
      const detail = event.detail || {};
      if (!this.matchesProduct(detail)) return;
      this.applySellingPlan(detail.sellingPlanId);
    }

    applySellingPlan(sellingPlanId) {
      if (!this.form) return;
      const planId = sellingPlanId ? String(sellingPlanId) : '';
      this.currentSellingPlanId = planId;
      let inputs = this.form.querySelectorAll('input[name="selling_plan"]');
      if (!inputs.length) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.className = 'hb-product-form__selling-plan';
        this.form.appendChild(input);
        inputs = [input];
      }
      inputs.forEach((input) => {
        if (!planId) {
          input.value = '';
          input.disabled = true;
          return;
        }
        input.disabled = false;
        input.value = planId;
      });
      this.sellingPlanInput = inputs[0];
    }

    onFormData(event) {
      const formData = event.formData;
      formData.delete('selling_plan');
      if (this.currentSellingPlanId) {
        formData.append('selling_plan', this.currentSellingPlanId);
      }
    }

    applyVariant(variant, variantId) {
      if (!this.variantInput) return;

      if (!variant && !variantId) {
        this.variantInput.value = '';
        this.variantInput.disabled = true;
        this.setSubmitState(false, 'unavailable');
        return;
      }

      const id = variant ? variant.id : variantId;
      const available = variant ? Boolean(variant.available) : true;
      this.variantInput.value = id;
      this.variantInput.disabled = !available;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));

      if (!available) {
        this.setSubmitState(false, 'soldOut');
      } else {
        this.setSubmitState(true, 'addToCart');
      }
    }

    applyFallbackSellingPlan() {
      if (!this.sellingPlanInput) return;
      if (this.defaultToSubscription && this.defaultSellingPlanId) {
        this.applySellingPlan(this.defaultSellingPlanId);
      } else {
        this.applySellingPlan('');
      }
    }

    setSubmitState(enabled, labelKey) {
      if (this.submitButton) {
        this.submitButton.disabled = !enabled;
      }
      if (this.submitLabel && window.variantStrings && labelKey && window.variantStrings[labelKey]) {
        this.submitLabel.textContent = window.variantStrings[labelKey];
      }
      if (this.buyNowButton) {
        this.buyNowButton.disabled = !enabled;
      }
    }

    onBuyNowClick(event) {
      event.preventDefault();
      if (!this.variantInput || this.variantInput.disabled || !this.variantInput.value) return;

      const variantId = this.variantInput.value;
      const items = [
        {
          id: Number(variantId),
          quantity: 1,
        },
      ];
      if (this.currentSellingPlanId) {
        items[0].selling_plan = Number(this.currentSellingPlanId);
      }

      const config = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

      const standardEvent = dispatchCartLinesAdd(this, [{ merchandiseId: variantId, quantity: 1 }]);

      fetch(`${config}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('Add failed');
          return resolveCartLinesFromAjax(standardEvent).then(() => {
            window.location.href = `${config}checkout`;
          });
        })
        .catch((error) => {
          dispatchCartErrorEvent(this, error && error.message ? error.message : 'Add failed', 'SERVICE_UNAVAILABLE');
          if (standardEvent) standardEvent.deferred.reject(error || new Error('Add failed'));
        });
    }
  }

  class HbSellingPlans extends HbElements.Component {
    onConnect() {
      this.productId = this.dataset.productId || '';
      this.requiresSellingPlan = this.boolAttr('requiresSellingPlan', false);
      this.showIntervals = this.boolAttr('showIntervals', false);
      this.plansByVariant = {};
      this.currentVariantId = this.dataset.initialVariantId || '';

      const plansNode = this.querySelector('[data-plans-by-variant]');
      if (plansNode) {
        try {
          this.plansByVariant = JSON.parse(plansNode.textContent);
        } catch (error) {
          this.plansByVariant = {};
        }
      }

      this.onHostClick = this.onHostClick.bind(this);
      this.onHostChange = this.onHostChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.addEventListener('click', this.onHostClick);
      this.addEventListener('change', this.onHostChange);
      document.addEventListener('hb:variant-change', this.onVariantChange);

      this.rebuildSelect();
      this.publish();
    }

    onDisconnect() {
      this.removeEventListener('click', this.onHostClick);
      this.removeEventListener('change', this.onHostChange);
      document.removeEventListener('hb:variant-change', this.onVariantChange);
    }

    getToggleButtons() {
      return Array.from(this.querySelectorAll('[data-purchase]'));
    }

    getIntervals() {
      return this.querySelector('[data-intervals]');
    }

    getSelect() {
      return this.querySelector('[data-selling-plan-select]');
    }

    onHostClick(event) {
      const button = event.target.closest('[data-purchase]');
      if (!button || !this.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      const purchase = button.dataset.purchase;
      if (!purchase) return;
      if (this.requiresSellingPlan && purchase === 'one_time') return;

      this.getToggleButtons().forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      const intervals = this.getIntervals();
      if (intervals) {
        const show = purchase === 'subscribe';
        intervals.hidden = !show;
        intervals.classList.toggle('is-hidden', !show);
      }

      this.publish({ standard: true });
    }

    onHostChange(event) {
      const select = event.target.closest('[data-selling-plan-select]');
      if (!select || !this.contains(select)) return;
      this.publish({ standard: true });
    }

    onVariantChange(event) {
      const detail = event.detail || {};
      const id = detailProductId(detail);
      if (this.productId && id && id !== String(this.productId)) return;
      if (detail.variantId != null) {
        this.currentVariantId = String(detail.variantId);
      }
      this.rebuildSelect();
      this.publish();
    }

    isSubscribeSelected() {
      const selected = this.getToggleButtons().find((button) => button.classList.contains('is-selected'));
      return selected ? selected.dataset.purchase === 'subscribe' : this.boolAttr('defaultSubscription', false);
    }

    plansForVariant(variantId) {
      const key = variantId ? String(variantId) : Object.keys(this.plansByVariant)[0];
      const plans = key && this.plansByVariant[key] ? this.plansByVariant[key] : [];
      return Array.isArray(plans) ? plans : [];
    }

    rebuildSelect() {
      const select = this.getSelect();
      if (!select || !this.showIntervals) return;
      const plans = this.plansForVariant(this.currentVariantId);
      const previous = select.value;
      select.innerHTML = '';
      plans.forEach((plan) => {
        const option = document.createElement('option');
        option.value = String(plan.id);
        option.textContent = plan.label;
        select.appendChild(option);
      });
      if (plans.length === 0) {
        select.value = '';
        return;
      }
      const match = plans.find((plan) => String(plan.id) === previous);
      select.value = match ? String(match.id) : String(plans[0].id);
    }

    resolvePlanId() {
      if (!this.isSubscribeSelected()) return '';
      const plans = this.plansForVariant(this.currentVariantId);
      const select = this.getSelect();
      if (this.showIntervals && select && select.value) {
        return select.value;
      }
      if (plans.length > 0) return String(plans[0].id);
      return '';
    }

    syncFormInput(sellingPlanId) {
      const host = this.closest('hb-product-form');
      const form =
        this.closest('form[data-type="add-to-cart-form"]') ||
        this.closest('form') ||
        (host && host.querySelector('form[data-type="add-to-cart-form"]'));
      if (!form) return;
      const inputs = form.querySelectorAll('input[name="selling_plan"]');
      const planId = sellingPlanId ? String(sellingPlanId) : '';
      inputs.forEach((input) => {
        if (!planId) {
          input.value = '';
          input.disabled = true;
          return;
        }
        input.disabled = false;
        input.value = planId;
      });
    }

    publish(options) {
      const sellingPlanId = this.resolvePlanId();
      this.syncFormInput(sellingPlanId);
      this.dispatchEvent(
        new CustomEvent('hb:selling-plan-change', {
          bubbles: true,
          composed: true,
          detail: {
            productId: this.productId,
            variantId: this.currentVariantId,
            sellingPlanId,
          },
        })
      );

      if (!options || !options.standard) return;

      const picker = matchingVariantPicker(this.productId);
      let variant = null;
      let selectedOptions = defaultSelectedOptions();
      if (picker && typeof picker.findVariant === 'function') {
        variant = picker.findVariant() || null;
        const fromPicker = namedSelectedOptions(picker);
        if (fromPicker.length) selectedOptions = fromPicker;
      }

      dispatchProductSelect(this, {
        variant,
        selectedOptions,
        detail: { sellingPlanId },
      });
    }
  }

  class HbVariantPicker extends HbElements.Component {
    onConnect() {
      this.productId = this.dataset.productId || '';
      this.optionInputs = Array.from(this.querySelectorAll('input[data-option-position]'));
      this.variantsNode = this.querySelector('[data-variants]');
      this.variants = [];

      if (this.variantsNode) {
        try {
          this.variants = JSON.parse(this.variantsNode.textContent);
        } catch (error) {
          this.variants = [];
        }
      }

      this.onOptionChange = this.onOptionChange.bind(this);
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', this.onOptionChange);
      });

      this.updateSelectionUi();
      this.publishVariant(this.findVariant());
    }

    onDisconnect() {
      this.optionInputs.forEach((input) => {
        input.removeEventListener('change', this.onOptionChange);
      });
    }

    onOptionChange() {
      this.updateSelectionUi();
      this.publishVariant(this.findVariant(), { standard: true });
    }

    updateSelectionUi() {
      this.optionInputs.forEach((input) => {
        const label = input.closest('.hb-variant-picker__value');
        if (!label) return;
        label.classList.toggle('is-selected', input.checked);
      });
    }

    selectedOptions() {
      const selected = {};
      this.optionInputs.forEach((input) => {
        if (!input.checked) return;
        selected[input.dataset.optionPosition] = input.dataset.optionValue;
      });
      return selected;
    }

    findVariant() {
      const selected = this.selectedOptions();
      return this.variants.find((variant) => {
        return Object.keys(selected).every((position) => {
          const index = Number(position) - 1;
          return variant.options[index] === selected[position];
        });
      });
    }

    publishVariant(variant, options) {
      this.dispatchEvent(
        new CustomEvent('hb:variant-change', {
          bubbles: true,
          composed: true,
          detail: {
            productId: this.productId,
            variantId: variant ? variant.id : null,
            variant: variant || null,
          },
        })
      );

      if (!options || !options.standard) return;

      dispatchProductSelect(this, {
        variant: variant || null,
        selectedOptions: namedSelectedOptions(this),
      });
    }
  }

  class HbProductPrice extends HbElements.Component {
    onConnect() {
      this.productId = this.dataset.productId || '';
      this.currentEl = this.querySelector('[data-price-current]');
      this.compareEl = this.querySelector('[data-price-compare]');
      this.priceMap = {};
      this.currentVariantId = this.dataset.initialVariantId || '';
      this.currentSellingPlanId = this.dataset.initialSellingPlanId || '';

      const mapNode = this.querySelector('[data-price-map]');
      if (mapNode) {
        try {
          this.priceMap = JSON.parse(mapNode.textContent);
        } catch (error) {
          this.priceMap = {};
        }
      }

      const plansHost = matchingSellingPlans(this.productId);
      if (plansHost && typeof plansHost.resolvePlanId === 'function') {
        this.currentSellingPlanId = plansHost.resolvePlanId() || '';
      }

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onSellingPlanChange = this.onSellingPlanChange.bind(this);
      document.addEventListener('hb:variant-change', this.onVariantChange);
      document.addEventListener('hb:selling-plan-change', this.onSellingPlanChange);
      this.render();
    }

    onDisconnect() {
      document.removeEventListener('hb:variant-change', this.onVariantChange);
      document.removeEventListener('hb:selling-plan-change', this.onSellingPlanChange);
    }

    matchesProduct(detail) {
      const id = detailProductId(detail);
      if (!this.productId || !id) return true;
      return id === String(this.productId);
    }

    onVariantChange(event) {
      const detail = event.detail || {};
      if (!this.matchesProduct(detail)) return;
      if (detail.variantId != null) {
        this.currentVariantId = String(detail.variantId);
      }
      this.render();
    }

    onSellingPlanChange(event) {
      const detail = event.detail || {};
      if (!this.matchesProduct(detail)) return;
      this.currentSellingPlanId = detail.sellingPlanId ? String(detail.sellingPlanId) : '';
      if (detail.variantId != null) {
        this.currentVariantId = String(detail.variantId);
      }
      this.render();
    }

    render() {
      const variantData = this.priceMap[this.currentVariantId] || this.priceMap[Object.keys(this.priceMap)[0]];
      if (!variantData || !this.currentEl) return;

      let price = variantData.price;
      let compareAt = variantData.compare_at;

      if (this.currentSellingPlanId && variantData.plans && variantData.plans[this.currentSellingPlanId]) {
        const planPrice = variantData.plans[this.currentSellingPlanId];
        price = planPrice.price;
        compareAt = planPrice.compare_at;
      }

      this.currentEl.textContent = price || '';
      if (this.compareEl) {
        if (compareAt) {
          this.compareEl.textContent = compareAt;
          this.compareEl.hidden = false;
        } else {
          this.compareEl.textContent = '';
          this.compareEl.hidden = true;
        }
      }
    }
  }

  HbElements.define('hb-product-form', HbProductForm);
  HbElements.define('hb-selling-plans', HbSellingPlans);
  HbElements.define('hb-variant-picker', HbVariantPicker);
  HbElements.define('hb-product-price', HbProductPrice);

  class HbStickyAtc extends HbElements.Component {
    onConnect() {
      this.productId = this.dataset.productId || '';
      this.position = this.dataset.position || 'bottom';
      this.secondaryAction = this.dataset.secondaryAction || 'buy_now';
      this.secondaryUrl = this.dataset.secondaryUrl || '';
      this.atcButton = this.querySelector('[data-sticky-atc]');
      this.secondaryButton = this.querySelector('button[data-sticky-secondary]');
      this.variantEl = this.querySelector('[data-sticky-variant]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.compareEl = this.querySelector('[data-sticky-compare]');
      this.productForm = this.findProductForm();
      this.observeTarget = this.findObserveTarget();

      this.priceMap = {};
      const mapNode = this.querySelector('[data-sticky-price-map]');
      if (mapNode) {
        try {
          this.priceMap = JSON.parse(mapNode.textContent);
        } catch (error) {
          this.priceMap = {};
        }
      }
      this.currentVariantId = this.dataset.initialVariantId || '';
      this.currentSellingPlanId = this.dataset.initialSellingPlanId || '';

      const plansHost = matchingSellingPlans(this.productId);
      if (plansHost && typeof plansHost.resolvePlanId === 'function') {
        this.currentSellingPlanId = plansHost.resolvePlanId() || this.currentSellingPlanId;
      }

      this.onAtcClick = this.onAtcClick.bind(this);
      this.onSecondaryClick = this.onSecondaryClick.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.syncBodyOffset = this.syncBodyOffset.bind(this);
      this.syncHeaderOffset = this.syncHeaderOffset.bind(this);
      this.onHeaderScroll = this.onHeaderScroll.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onSellingPlanChange = this.onSellingPlanChange.bind(this);

      document.addEventListener('hb:variant-change', this.onVariantChange);
      document.addEventListener('hb:selling-plan-change', this.onSellingPlanChange);

      if (this.atcButton) {
        this.atcButton.addEventListener('click', this.onAtcClick);
      }
      if (this.secondaryButton) {
        this.secondaryButton.addEventListener('click', this.onSecondaryClick);
      }

      if (this.observeTarget && typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(this.onIntersect, {
          root: null,
          threshold: 0,
        });
        this.observer.observe(this.observeTarget);
      } else {
        this.classList.add('is-visible');
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.syncBodyOffset);
        this.resizeObserver.observe(this);
      }

      if (this.position === 'top') {
        this.headerEl = document.querySelector('.section-header');
        window.addEventListener('scroll', this.onHeaderScroll, { passive: true });
        window.addEventListener('resize', this.onHeaderScroll, { passive: true });
        this.syncHeaderOffset();
      }

      this.syncDisabled();
      this.syncBodyOffset();
      this.render();
    }

    onDisconnect() {
      document.removeEventListener('hb:variant-change', this.onVariantChange);
      document.removeEventListener('hb:selling-plan-change', this.onSellingPlanChange);
      if (this.atcButton) {
        this.atcButton.removeEventListener('click', this.onAtcClick);
      }
      if (this.secondaryButton) {
        this.secondaryButton.removeEventListener('click', this.onSecondaryClick);
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      window.removeEventListener('scroll', this.onHeaderScroll);
      window.removeEventListener('resize', this.onHeaderScroll);
      if (this.headerFrame) {
        cancelAnimationFrame(this.headerFrame);
        this.headerFrame = null;
      }
      this.clearBodyOffset();
    }

    findProductForm() {
      if (!this.productId) {
        return document.querySelector('hb-product-form');
      }
      return document.querySelector(`hb-product-form[data-product-id="${this.productId}"]`);
    }

    findObserveTarget() {
      if (!this.productForm) return null;
      return (
        this.productForm.querySelector('.hb-product-form__submit') ||
        this.productForm.querySelector('[type="submit"][name="add"]')
      );
    }

    matchesProduct(detail) {
      const id = detailProductId(detail);
      if (!this.productId || !id) return true;
      return id === String(this.productId);
    }

    onVariantChange(event) {
      const detail = event.detail || {};
      if (!this.matchesProduct(detail)) return;
      if (detail.variantId != null) {
        this.currentVariantId = String(detail.variantId);
      }
      this.render();
      this.syncDisabled();
    }

    onSellingPlanChange(event) {
      const detail = event.detail || {};
      if (!this.matchesProduct(detail)) return;
      this.currentSellingPlanId = detail.sellingPlanId ? String(detail.sellingPlanId) : '';
      if (detail.variantId != null) {
        this.currentVariantId = String(detail.variantId);
      }
      this.render();
    }

    render() {
      const keys = Object.keys(this.priceMap);
      if (!keys.length) return;
      const variantData = this.priceMap[this.currentVariantId] || this.priceMap[keys[0]];
      if (!variantData) return;

      let price = variantData.price;
      let compareAt = variantData.compare_at;

      if (this.currentSellingPlanId && variantData.plans && variantData.plans[this.currentSellingPlanId]) {
        const planPrice = variantData.plans[this.currentSellingPlanId];
        price = planPrice.price;
        compareAt = planPrice.compare_at;
      }

      if (this.variantEl) this.variantEl.textContent = variantData.title || '';
      if (this.priceEl) this.priceEl.textContent = price || '';
      if (this.compareEl) {
        if (compareAt) {
          this.compareEl.textContent = compareAt;
          this.compareEl.hidden = false;
        } else {
          this.compareEl.textContent = '';
          this.compareEl.hidden = true;
        }
      }
    }

    onHeaderScroll() {
      if (this.headerFrame) return;
      this.headerFrame = requestAnimationFrame(() => {
        this.headerFrame = null;
        this.syncHeaderOffset();
      });
    }

    // The header sits below the announcement bar until it scrolls away, and hides on scroll-down,
    // so a top bar has to follow the header's live bottom edge, not its height.
    syncHeaderOffset() {
      if (this.position !== 'top') return;
      const rect = this.headerEl ? this.headerEl.getBoundingClientRect() : null;
      const bottom = rect ? Math.max(0, Math.round(rect.bottom)) : 0;
      this.style.setProperty('--hb-satc-header-offset', `${bottom}px`);
    }

    syncBodyOffset() {
      if (this.position !== 'bottom') {
        this.clearBodyOffset();
        return;
      }
      const height = this.classList.contains('is-visible') ? this.offsetHeight : 0;
      document.documentElement.style.setProperty('--hb-sticky-atc-offset', `${height}px`);
    }

    clearBodyOffset() {
      document.documentElement.style.removeProperty('--hb-sticky-atc-offset');
    }

    onIntersect(entries) {
      const entry = entries[0];
      if (!entry) return;
      const show = !entry.isIntersecting;
      this.classList.toggle('is-visible', show);
      if (show) this.syncDisabled();
      this.syncBodyOffset();
    }

    syncDisabled() {
      const submit = this.findObserveTarget();
      const disabled = submit ? submit.disabled : false;
      if (this.atcButton) this.atcButton.disabled = disabled;
      if (this.secondaryButton) this.secondaryButton.disabled = disabled;
    }

    onAtcClick(event) {
      event.preventDefault();
      this.productForm = this.productForm || this.findProductForm();
      const submit = this.findObserveTarget();
      if (!submit || submit.disabled) return;
      submit.click();
    }

    onSecondaryClick(event) {
      event.preventDefault();
      this.productForm = this.productForm || this.findProductForm();
      if (!this.productForm) return;

      if (this.secondaryAction === 'url' && this.secondaryUrl) {
        window.location.href = this.secondaryUrl;
        return;
      }

      const buyNow = this.productForm.querySelector('[data-buy-now]');
      if (buyNow && !buyNow.disabled) {
        buyNow.click();
        return;
      }

      if (typeof this.productForm.onBuyNowClick === 'function') {
        this.productForm.onBuyNowClick(event);
      }
    }
  }

  HbElements.define('hb-sticky-atc', HbStickyAtc);

  class HbAddAll extends HbElements.Component {
    onConnect() {
      this.button = this.querySelector('[data-add-all]');
      this.spinner = this.querySelector('.loading__spinner');
      this.items = this.readItems();
      this.onClick = this.onClick.bind(this);

      if (this.button) {
        this.button.addEventListener('click', this.onClick);
        if (!this.items.length) {
          this.button.disabled = true;
        }
      }
    }

    onDisconnect() {
      if (this.button) {
        this.button.removeEventListener('click', this.onClick);
      }
    }

    readItems() {
      const node = this.querySelector('[data-add-all-items]');
      if (!node) return [];
      try {
        const items = JSON.parse(node.textContent);
        return Array.isArray(items) ? items : [];
      } catch (error) {
        return [];
      }
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.classList.toggle('loading', loading);
      this.button.setAttribute('aria-disabled', loading ? 'true' : 'false');
      if (loading) {
        this.button.setAttribute('disabled', 'disabled');
      } else if (this.items.length) {
        this.button.removeAttribute('disabled');
      }
      if (this.spinner) {
        this.spinner.classList.toggle('hidden', !loading);
      }
    }

    onClick(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled || !this.items.length) return;

      this.setLoading(true);

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items: this.items };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      const config = fetchConfig('javascript');
      config.body = JSON.stringify(body);

      const standardEvent = dispatchCartLinesAdd(
        this,
        this.items.map((item) => ({
          merchandiseId: item.id,
          quantity: item.quantity || 1,
        }))
      );

      fetch(routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'hb-add-all',
              errors: response.errors || response.description,
              message: response.message,
            });
            dispatchCartErrorEvent(this, response.description || response.message, 'INVALID');
            if (standardEvent) standardEvent.deferred.reject(new Error(response.description || response.message));
            return;
          }

          if (!cart) {
            return resolveCartLinesFromAjax(standardEvent).then(() => {
              window.location = window.routes.cart_url;
            });
          }

          return resolveCartLinesFromAjax(standardEvent, response).then(() => {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'hb-add-all',
              cartData: response,
            });

            if (cart.classList.contains('is-empty')) {
              cart.classList.remove('is-empty');
            }
            cart.renderContents(response);
          });
        })
        .catch((error) => {
          dispatchCartErrorEvent(this, (error && error.message) || 'Network error', 'SERVICE_UNAVAILABLE');
          if (standardEvent) standardEvent.deferred.reject(error || new Error('Network error'));
        })
        .finally(() => {
          this.setLoading(false);
        });
    }
  }

  HbElements.define('hb-add-all', HbAddAll);
})();
